import { isParserDebugEnabled } from 'utils/natural-language-filter-parser';
import type {
  ParserPipelineFlags,
  ParseNaturalLanguageFiltersResult,
} from 'utils/natural-language-filter-parser';

const PARSER_ENDPOINT = '/api/natural-language-filters';

/**
 * What this panel does not need from the parser.
 *
 * It puts predictions in front of the user and navigates to the one they pick,
 * so a place lookup inside the parse would ask the same service the same
 * question and answer it worse — guessing where the user already chose.
 */
const PARSER_FLAGS: Partial<ParserPipelineFlags> = {
  locationIdentification: false,
  generateTags: false,
};
const DEBUG_NS = '[nl-filter-parser] remote';

/**
 * The parser's own trace runs on the server, so the browser console would
 * otherwise show nothing for a query. This is the client-side half: what was
 * sent, what came back, and the round trip including the LLM step.
 */
function debugRemoteParse(
  query: string,
  result: ParseNaturalLanguageFiltersResult | null,
  durationMs: number
): void {
  if (!isParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.debug(DEBUG_NS, {
    query,
    filters: result?.filters ?? null,
    tags: result?.tags ?? null,
    unparsed: result?.unparsed ?? null,
    roundTripMs: Number(durationMs.toFixed(1)),
  });
}

/**
 * Parses through the API route instead of in the browser.
 *
 * The endpoint runs the same parser with `aiFallback` on, which the client
 * cannot do itself: the LLM credentials are server-only, so an in-browser parse
 * silently skips the model. Tags are handled separately by the smart-tags
 * service (see `PARSER_FLAGS.generateTags`), so this call only returns filters.
 *
 * Returns null on a failed request, which the caller reads the same way it
 * reads an unparseable query.
 */
export async function parseNaturalLanguageFiltersRemote(
  query: string
): Promise<ParseNaturalLanguageFiltersResult | null> {
  const started = performance.now();
  const response = await fetch(PARSER_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, flags: PARSER_FLAGS }),
  });
  if (!response.ok) {
    debugRemoteParse(query, null, performance.now() - started);
    return null;
  }
  const body: unknown = await response.json();
  const result =
    (body as { result?: ParseNaturalLanguageFiltersResult | null } | null)?.result ?? null;
  debugRemoteParse(query, result, performance.now() - started);
  return result;
}

export default parseNaturalLanguageFiltersRemote;
